import React from 'react';
import gameSessionsApi, { gameErrorMessage } from '../../utils/gameSessionsApi';
import JoinGame from './JoinGame';
import {
  Bubble,
  CardLabel,
  FunButton,
  FunCard,
  GameHero,
  GameModal,
  Pill,
  Spinner,
  entranceStyle,
  gradient,
} from './GameUI';
import { activityEmoji, tilePalette } from './activityTheme';
import { groupLanguages } from './gameLanguages';
import PictureSetPicker from './PictureSetPicker';

const DURATION_OPTIONS = [
  { seconds: 180, label: '3 min', emoji: '⚡', name: 'Quick' },
  { seconds: 300, label: '5 min', emoji: '🎯', name: 'Classic' },
  { seconds: 600, label: '10 min', emoji: '🏃', name: 'Marathon' },
];

function packEmoji(pack) {
  return activityEmoji({ id: pack.activity_id, title: pack.title });
}

const PackCard = ({ pack, index, onClick }) => {
  const palette = tilePalette(index);
  return (
    <button
      type="button"
      className="gpPack gpEnter"
      style={entranceStyle(index + 1)}
      onClick={onClick}
      aria-label={`Choose ${pack.title}`}
    >
      <span
        className="gpPack__tile"
        style={{ background: gradient(palette.tile), transform: `rotate(${index % 2 === 0 ? -6 : 6}deg)` }}
      >
        {(pack.teaser_image_url || pack.cover_image_url)
          ? <img className="gpPack__cover" src={pack.teaser_image_url || pack.cover_image_url} alt="" />
          : <span className="gpPack__emoji" aria-hidden="true">{packEmoji(pack)}</span>}
      </span>
      <span className="gpPack__text">
        <span className="gpPack__title">{pack.title}</span>
        <span className="gpPack__meta">
          <Pill emoji="🖼️" text={`${pack.picture_count} pictures`} />
          {pack.has_practice && <Pill emoji="🧪" text="Practice rounds" />}
        </span>
      </span>
      <span className="gpPack__arrow" style={{ backgroundColor: palette.soft, color: palette.accent }} aria-hidden="true">
        →
      </span>
    </button>
  );
};

const DurationTile = ({ option, selected, onClick }) => (
  <button
    type="button"
    role="radio"
    aria-checked={selected}
    aria-label={`${option.label} ${option.name}`}
    className={`gpDuration${selected ? ' gpDuration--selected' : ''}`}
    onClick={onClick}
  >
    <span className="gpDuration__emoji" aria-hidden="true">{option.emoji}</span>
    <span className="gpDuration__label">{option.label}</span>
    <span className="gpDuration__name">{option.name}</span>
  </button>
);

const LanguageRow = ({ language, tag, selected, onClick }) => (
  <button
    type="button"
    className={`gpLanguageList__item${selected ? ' gpLanguageList__item--selected' : ''}`}
    onClick={onClick}
    aria-pressed={selected}
  >
    <span className="gpLanguageList__text">
      <span className="gpLanguageList__name">{language.language}</span>
      {!!language.native && language.native !== language.language && (
        <span className="gpLanguageList__native">{language.native}</span>
      )}
    </span>
    {!!tag && (
      <span className={`gpLanguageList__tag gpLanguageList__tag--${tag.toLowerCase()}`}>{tag}</span>
    )}
    <span className="gpLanguageList__check" aria-hidden="true">{selected ? '✓' : ''}</span>
  </button>
);

const LanguageSection = ({ emoji, title, rows, selectedId, onChoose }) => (
  <div className="gpLanguageList__section">
    <h3 className="gpLanguageList__heading">
      <span aria-hidden="true">{emoji}</span> {title}
    </h3>
    {rows.map(({ language, tag }) => (
      <LanguageRow
        key={language.id}
        language={language}
        tag={tag}
        selected={language.id === selectedId}
        onClick={() => onChoose(language)}
      />
    ))}
  </div>
);

class LanguagePicker extends React.Component {
  state = { open: false, search: '' };

  open = () => this.setState({ open: true, search: '' });

  close = () => this.setState({ open: false });

  choose = (language) => {
    this.props.onChange(language);
    this.close();
  };

  renderSheet() {
    const { language, languages, userLanguages } = this.props;
    const { mine, others } = groupLanguages(languages, userLanguages, this.state.search);
    const selectedId = language ? language.id : null;

    return (
      <GameModal title="Pick a language" hideTitle className="gpModal__card--sheet" onClose={this.close}>
        <div className="gpLanguageSheet__header">
          <div>
            <h2 className="gpModal__title gpLanguageSheet__title">Pick a language 🗣️</h2>
            <p className="gpSubtitle">You&apos;ll both speak only this language during the game.</p>
          </div>
          <button type="button" className="gpLanguageSheet__close" onClick={this.close} aria-label="Close">✕</button>
        </div>
        <label className="gpLanguageSheet__search">
          <span className="gpLanguageSheet__searchIcon" aria-hidden="true">🔍</span>
          <input
            type="search"
            value={this.state.search}
            onChange={(event) => this.setState({ search: event.target.value })}
            placeholder="Search languages"
            aria-label="Search languages"
            autoComplete="off"
            autoFocus
          />
        </label>
        <div className="gpLanguageList">
          {mine.length > 0 && (
            <LanguageSection emoji="⭐" title="Your languages" rows={mine} selectedId={selectedId} onChoose={this.choose} />
          )}
          {others.length > 0 && (
            <LanguageSection
              emoji="🌍"
              title={mine.length > 0 ? 'All languages' : 'Languages'}
              rows={others}
              selectedId={selectedId}
              onChoose={this.choose}
            />
          )}
          {mine.length === 0 && others.length === 0 && (
            <div className="gpLanguageList__empty">
              <span aria-hidden="true">🔍</span>
              <p>No languages match &ldquo;{this.state.search.trim()}&rdquo;</p>
            </div>
          )}
        </div>
      </GameModal>
    );
  }

  render() {
    const { language } = this.props;
    return (
      <div>
        <button
          type="button"
          className="gpLanguage"
          onClick={this.open}
          aria-label="Change game language"
        >
          <span className="gpLanguage__flag" aria-hidden="true">🌍</span>
          <span className="gpLanguage__text">
            <span className="gpLanguage__caption">Speak only in</span>
            <span className="gpLanguage__name">{language ? language.language : 'Choose a language'}</span>
          </span>
          <span className="gpLanguage__change">Change ▾</span>
        </button>
        {this.state.open && this.renderSheet()}
      </div>
    );
  }
}

class NewGame extends React.Component {
  state = {
    packs: null,
    packsError: '',
  };

  componentDidMount() {
    this.loadPacksIfNeeded();
  }

  componentDidUpdate() {
    this.loadPacksIfNeeded();
  }

  loadPacksIfNeeded() {
    if (this.props.joinOnly || this.props.pack || this.state.packs !== null || this.loadingPacks) {
      return;
    }
    this.loadingPacks = true;
    gameSessionsApi.packs()
      .then((packs) => this.setState({ packs }))
      .catch((error) => this.setState({ packs: [], packsError: gameErrorMessage(error) }))
      .finally(() => { this.loadingPacks = false; });
  }

  renderPackHero() {
    return (
      <GameHero
        emoji="🎮"
        kicker="LET'S PLAY"
        title="Pick a game"
        subtitle="Choose a picture pack, then invite your partner with a code."
      />
    );
  }

  renderPackList() {
    return (
      <div>
        <h2 className="gpSplit__heading"><span aria-hidden="true">🎮</span> Choose a game</h2>
        {this.state.packs === null && (
          <div className="gpLoading"><Spinner /></div>
        )}
        {this.state.packsError !== '' && <Bubble emoji="😬" text={this.state.packsError} />}
        {(this.state.packs || []).map((pack, index) => (
          <PackCard
            key={pack.activity_id}
            pack={pack}
            index={index}
            onClick={() => this.props.onChoosePack(pack)}
          />
        ))}
      </div>
    );
  }

  renderSetupHero() {
    const { pack } = this.props;
    return (
      <GameHero
        emoji={packEmoji(pack)}
        image={pack.teaser_image_url}
        kicker="GET READY"
        title={pack.title}
        subtitle={pack.body || null}
      >
        <button type="button" className="gpSwitchGame" onClick={this.props.onClearPack}>
          <Pill light emoji="🔄" text="Switch game" />
        </button>
      </GameHero>
    );
  }

  renderSetupOptions() {
    const { pack } = this.props;
    return (
      <div>
        <h2 className="gpSplit__heading"><span aria-hidden="true">🚀</span> Start a new game</h2>
        <FunCard entranceIndex={1}>
          <CardLabel emoji="🗣️" text="Game language" />
          <LanguagePicker
            language={this.props.language}
            languages={this.props.languages}
            userLanguages={this.props.userLanguages}
            onChange={this.props.onLanguage}
          />
        </FunCard>

        <FunCard entranceIndex={2}>
          <CardLabel emoji="⏱️" text="Game length" />
          <div className="gpDurationRow" role="radiogroup" aria-label="Game length">
            {DURATION_OPTIONS.map((option) => (
              <DurationTile
                key={option.seconds}
                option={option}
                selected={option.seconds === this.props.durationSeconds}
                onClick={() => this.props.onDuration(option.seconds)}
              />
            ))}
          </div>
        </FunCard>

        <FunCard entranceIndex={3}>
          <CardLabel emoji="🖼️" text="Picture pack" />
          <PictureSetPicker
            always
            source={pack}
            selected={this.props.pictureSet}
            extendedUnlocked={this.props.premiumUser}
            locked={!this.props.premiumUser}
            disabled={this.props.busy}
            onChange={this.props.onPictureSet}
            onUpgrade={this.props.onUpgrade}
          />
        </FunCard>

        {this.props.error !== '' && <Bubble emoji="😬" text={this.props.error} />}

        <FunButton
          emoji="🚀"
          text="Create game"
          loading={this.props.busy}
          onClick={this.props.onCreate}
        />
      </div>
    );
  }

  renderJoinHero() {
    return (
      <GameHero
        emoji="🎟️"
        kicker="GOT A CODE?"
        title="Join your partner"
        subtitle="Pop in the 4-character code your partner shared to jump into their game."
      />
    );
  }

  render() {
    const { joinOnly, pack } = this.props;
    let mode = 'packs';
    if (joinOnly) mode = 'join';
    else if (pack) mode = 'setup';

    return (
      <div className={`gpSplit gpSplit--${mode}`}>
        {this.props.notice !== '' && (
          <div className="gpSplit__top"><Bubble emoji="👋" text={this.props.notice} /></div>
        )}

        <div className="gpSplit__hero">
          {mode === 'join' && this.renderJoinHero()}
          {mode === 'setup' && this.renderSetupHero()}
          {mode === 'packs' && this.renderPackHero()}
        </div>

        {mode !== 'join' && (
          <div className="gpSplit__main">
            {mode === 'setup' ? this.renderSetupOptions() : this.renderPackList()}
          </div>
        )}

        {mode !== 'join' && (
          <div className="gpOr gpSplit__or" aria-hidden="true">
            <span className="gpOr__line" />
            <span className="gpOr__text">or</span>
            <span className="gpOr__line" />
          </div>
        )}

        <div className="gpSplit__aside">
          <JoinGame
            featured={joinOnly}
            initialCode={this.props.initialCode}
            onJoin={this.props.onJoin}
            busy={this.props.busy}
            error={this.props.joinError}
          />
        </div>
      </div>
    );
  }
}

export default NewGame;
